import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Spacer,
  Text,
  useToast,
  useDisclosure,
  Input,
} from "@chakra-ui/react";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { Field, Form, Formik } from "formik";
import Select from "react-select";
import { useCuisinesMaster } from "../../store/cuisines/reducer";
import {
  REQUEST_CREATE_MASTER_CUISINES,
  REQUEST_MASTER_CUISINES,
  REQUEST_UPDATE_MASTER_CUISINES,
  SET_MESSAGE_MASTER_CUISINES,
} from "../../store/cuisines/cuisineActionTypes";
import FaqAddCategoryModal from "../modals/FaqAddCategoryModal";

interface FormFieldProps {
  required?: boolean;
  name: string;
  fieldName: string;
  placeholder: string;
  type?: string;
  error?: string;
}

const FormField: React.FC<FormFieldProps> = ({
  required,
  name,
  fieldName,
  placeholder,
  type = "text",
  error,
}) => (
  <FormControl isRequired={required} mb={4}>
    <FormLabel htmlFor={fieldName}>{name}</FormLabel>
    <Field name={fieldName}>
      {({ field }: any) => (
        <Input
          {...field}
          id={fieldName}
          placeholder={placeholder}
          type={type}
          border="1px solid #E0E0E0"
          _focus={{
            borderColor: "#E24441",
            boxShadow: "0 0 0 1px #E24441",
          }}
        />
      )}
    </Field>
    {error && (
      <Text color="red.500" fontSize="sm">
        {error}
      </Text>
    )}
  </FormControl>
);

interface Props {
  faqs?: any;
  isOpen: boolean;
  onClose: () => void;
  cuisineData?: any;
}

const addSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
});

const CuisineEditDrawer: React.FC<Props> = ({
  cuisineData,
  isOpen,
  onClose,
}) => {
  const dispatch = useDispatch();
  const { busy, masterCuisines } = useCuisinesMaster();
  const toast = useToast();
  console.log("mastersjfcnds", cuisineData);

  const initialValue = useMemo(() => {
    if (cuisineData) {
      return {
        name: cuisineData.name || "",
      };
    }
    return {
      name: "",
    };
  }, [cuisineData]);

  const onSubmit = (values: Record<string, any>) => {
    const payload: Record<string, any> = {
      name: values.name,
    };

    if (cuisineData) {
      const data = { payload, _id: cuisineData._id };
      dispatch({
        type: REQUEST_UPDATE_MASTER_CUISINES,
        data,
        onSuccess: () => {
          dispatch({ type: REQUEST_MASTER_CUISINES });
          onClose();
        },
      });
      toast({
        title: "Success",
        description: "Cuisine has been updated",
        status: "success",
        isClosable: true,
        position: "top-right",
      });
    } else {
      dispatch({
        type: REQUEST_CREATE_MASTER_CUISINES,
        payload,
        onSuccess: () => {
          dispatch({ type: REQUEST_MASTER_CUISINES });
          onClose();
        },
      });
      toast({
        title: "Success",
        description: "New Cuisines has been created",
        status: "success",
        isClosable: true,
        position: "top-right",
      });
    }
  };

  const prevCuisines = useMemo(() => {
    return masterCuisines;
  }, []);

  useEffect(() => {
    if (cuisineData) {
      const cuisinesFromStore = masterCuisines.find(
        (r: any) => r._id === cuisineData._id
      );

      if (cuisinesFromStore !== cuisineData) {
        toast({
          title: "Success",
          description: "Cuisines has been updated",
          status: "success",
          isClosable: true,
          position: "top-right",
        });
        onClose();
      }
    } else {
      if (masterCuisines.length !== prevCuisines.length) {
        toast({
          title: "Success",
          description: "New Cuisines has been created",
          status: "success",
          isClosable: true,
          position: "top-right",
        });
        onClose();
      }
    }
  }, [masterCuisines, prevCuisines, cuisineData, onClose, toast]);

  const customStyles = {
    control: (base: any, state: any) => ({
      ...base,
      border: state.isFocused ? "1px solid #E24441" : "1px solid #E0E0E0",
      boxShadow: state.isFocused ? "0 0 0 1px #E24441" : "none",
      "&:hover": {
        borderColor: "#E24441",
      },
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
  };
  return (
    <Formik
      initialValues={initialValue}
      validationSchema={addSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({ values, setFieldValue, errors, touched, submitForm }: any) => (
        <Form>
          <Drawer
            isOpen={isOpen}
            placement="right"
            size="lg"
            onClose={() => {
              onClose();
              dispatch({ type: SET_MESSAGE_MASTER_CUISINES });
            }}
          >
            <DrawerOverlay />
            <DrawerContent>
              <DrawerHeader
                backgroundColor="#E244411A"
                borderBottom="1px solid #E0E0E0"
              >
                <Flex>
                  {cuisineData ? "Edit Cuisine" : "Add Cuisine"}
                  <Spacer />
                  <img
                    src="/icons/close.svg"
                    alt=""
                    style={{ cursor: "pointer" }}
                    onClick={onClose}
                  />
                </Flex>
              </DrawerHeader>
              <DrawerBody>
                <FormField
                  required
                  name="Cuisine"
                  fieldName="name"
                  placeholder="Enter cuisine"
                  type="text"
                  error={touched.name && (errors.name as string)}
                />
              </DrawerBody>
              <DrawerFooter>
                <Button
                  variant="outline"
                  mr={3}
                  onClick={() => {
                    onClose();
                    dispatch({ type: SET_MESSAGE_MASTER_CUISINES });
                  }}
                  disabled={busy}
                >
                  Close
                </Button>
                <Button
                  backgroundColor="#E24441"
                  color="white"
                  _hover={{
                    backgroundColor: "#C33A37",
                  }}
                  onClick={submitForm}
                  disabled={busy}
                >
                  {cuisineData ? "Update" : "Add Cuisine"}
                </Button>
              </DrawerFooter>
            </DrawerContent>
          </Drawer>
        </Form>
      )}
    </Formik>
  );
};

export default CuisineEditDrawer;
